import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate, useParams } from 'react-router-dom';
import FullscreenError from '@/components/utils/FullscreenError';
import CreateModuleModal from '@/components/admin/academy/CreateModuleModal.tsx';
import EditModuleModal from '@/components/admin/academy/EditModuleModal.tsx';
import EditCourseModal from '@/components/admin/academy/EditCourseModal.tsx';
import CourseUsersTab from '@/components/admin/academy/CourseUsersTab.tsx';
import MarkdownRenderer from '@/components/ui/MarkdownRenderer';
import { faSpinner, faArrowLeft, faPlus, faEdit, faTrash, faBook, faVideo, faRocket, faEyeSlash, faUsers } from '@/fontawesome';
import { useUser } from '@/contexts/UserContext';
import { handleAPIError, showSuccessToast, showErrorToast } from '@/utils/toasts';
import { formatDate } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import PublishModal from '@/components/PublishModal';
import type { Course, Module, CourseID, ModuleID } from '@/models';

type CourseDetailTab = 'modules' | 'users';

const PAGE_SIZE = 50;

export default function AdminCourseDetail() {
  const { isStaff } = useUser();
  const navigate = useNavigate();
  const { courseId } = useParams<{ courseId: string }>();
  const [course, setCourse] = useState<Course | null>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [criticalError, setCriticalError] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalModules, setTotalModules] = useState(0);
  const [activeTab, setActiveTab] = useState<CourseDetailTab>('modules');
  const [isCreateModuleModalOpen, setIsCreateModuleModalOpen] = useState(false);
  const [isEditModuleModalOpen, setIsEditModuleModalOpen] = useState(false);
  const [editingModule, setEditingModule] = useState<Module | null>(null);
  const [deletingModule, setDeletingModule] = useState<ModuleID | null>(null);
  const [publishingModule, setPublishingModule] = useState<ModuleID | null>(null);
  const [publishModalModule, setPublishModalModule] = useState<Module | null>(null);
  const [isEditCourseModalOpen, setIsEditCourseModalOpen] = useState(false);
  const [isPublishCourseModalOpen, setIsPublishCourseModalOpen] = useState(false);
  const [publishingCourse, setPublishingCourse] = useState(false);
  const [deletingCourse, setDeletingCourse] = useState(false);

  useEffect(() => {
    if (courseId) {
      fetchCourseAndModules();
    }
  }, [courseId, currentPage]);

  const fetchCourseAndModules = async () => {
    if (!courseId) {
      console.error('No courseId provided');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);
      
      // Fetch course details and modules in parallel
      const [courseResponse, modulesResponse] = await Promise.all([
        APIRoutes.getCourse(courseId as CourseID),
        APIRoutes.listModules(courseId as CourseID, {
          limit: PAGE_SIZE,
          next: currentPage > 1 ? `$${currentPage}` : undefined
        })
      ]);

      setCourse(courseResponse);
      setTotalModules(modulesResponse.total);

      // Validate and filter modules response
      const modules = modulesResponse.modules || [];

      const validModules = modules.filter((module: Module | null | undefined) => {
        return module !== null && module !== undefined && module && typeof module.id === 'string' && module.id.length > 0;
      });

      setModules(validModules);
    } catch (err) {
      console.error('Critical error loading course details:', err);
      setError('Backend nicht erreichbar oder Fehler beim Laden der Kursdetails');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleModuleCreated = (newModule: Module) => {
    if (newModule && newModule.id) {
      setModules(prev => {
        if (!Array.isArray(prev)) return [newModule];
        return [...prev.filter(m => m && m.id), newModule];
      });
      setTotalModules(prev => prev + 1);
      if (course) {
        setCourse(prev => prev ? { ...prev, moduleCount: (prev.moduleCount ?? 0) + 1 } : prev);
      }
      showSuccessToast('Modul erfolgreich erstellt');
    } else {
      console.error('Invalid module passed to handleModuleCreated:', newModule);
    }
  };

  const handleEditModule = (module: Module) => {
    setEditingModule(module);
    setIsEditModuleModalOpen(true);
  };

  const handleModuleUpdated = (updatedModule: Module) => {
    // Guard against undefined updatedModule
    if (!updatedModule || !updatedModule.id) {
      console.error('handleModuleUpdated called with invalid updatedModule:', updatedModule);
      showErrorToast('Fehler: Ungültiges Modul zum Update erhalten');
      return;
    }
    
    setModules(prevModules => {
      if (!Array.isArray(prevModules)) {
        return [];
      }

      const filteredModules = prevModules.filter(module => {
        return module !== null && module !== undefined && module && typeof module.id === 'string' && module.id.length > 0;
      });

      return filteredModules.map(module => {
        if (!module || !module.id) {
          console.error('ERROR: Module without id found after filtering:', module);
          // Return a default module structure instead of the invalid one
          return {
            id: 'invalid-' + Date.now(),
            courseID: courseId || '',
            title: 'Invalid Module',
            description: '',
            order: 0,
            published: false,
            thumbnail: null,
            videoCount: 0,
            created: new Date().toISOString(),
            updated: null,
            deleted: null
          } as Module;
        }
        return module.id === updatedModule.id ? updatedModule : module;
      });
    });
    showSuccessToast('Modul erfolgreich aktualisiert');
  };

  const handleDeleteModule = async (moduleId: ModuleID) => {
    if (!courseId) return;

    if (!confirm('Sind Sie sicher, dass Sie dieses Modul löschen möchten? Alle Videos werden ebenfalls gelöscht.')) {
      return;
    }

    try {
      setDeletingModule(moduleId);
      await APIRoutes.deleteModule(moduleId);
      setModules(prev => {
        if (!Array.isArray(prev)) return [];
        return prev.filter(module => module && module.id && module.id !== moduleId);
      });
      setTotalModules(prev => prev - 1);
      if (course) {
        setCourse(prev => prev ? { ...prev, moduleCount: (prev.moduleCount || 0) - 1 } : prev);
      }
      showSuccessToast('Modul erfolgreich gelöscht');
    } catch (err) {
      console.error('Error deleting module:', err);
      showErrorToast('Fehler beim Löschen des Moduls');
      handleAPIError(err);
    } finally {
      setDeletingModule(null);
    }
  };

  const handlePublishModule = async () => {
    if (!publishModalModule) return;

    try {
      setPublishingModule(publishModalModule.id);
      const updatedModule = await APIRoutes.publishModule(publishModalModule.id);
      setModules(prev => prev.map(module => module.id === updatedModule.id ? updatedModule : module));
      showSuccessToast('Modul erfolgreich veröffentlicht');
    } catch (err) {
      console.error('Error publishing module:', err);
      showErrorToast('Fehler beim Veröffentlichen des Moduls');
      handleAPIError(err);
      throw err;
    } finally {
      setPublishingModule(null);
    }
  };

  const handleUnpublishModule = async (moduleId: ModuleID) => {
    if (!confirm('Möchten Sie dieses Modul wirklich zurückziehen?')) {
      return;
    }

    try {
      setPublishingModule(moduleId);
      const updatedModule = await APIRoutes.unpublishModule(moduleId);
      setModules(prev => prev.map(module => module.id === updatedModule.id ? updatedModule : module));
      showSuccessToast('Modul erfolgreich zurückgezogen');
    } catch (err) {
      console.error('Error unpublishing module:', err);
      showErrorToast('Fehler beim Zurückziehen des Moduls');
      handleAPIError(err);
    } finally {
      setPublishingModule(null);
    }
  };

  const handleCourseUpdated = (updatedCourse: Course) => {
    if (updatedCourse !== null && updatedCourse.id) {
      setCourse(updatedCourse);
      showSuccessToast('Kurs erfolgreich aktualisiert');
    }
  };

  const handlePublishCourse = async () => {
    if (course === null) return;

    try {
      setPublishingCourse(true);
      const updatedCourse = await APIRoutes.publishCourse(course.id);
      setCourse(updatedCourse);
      showSuccessToast('Kurs erfolgreich veröffentlicht');
    } catch (err) {
      console.error('Error publishing course:', err);
      showErrorToast('Fehler beim Veröffentlichen des Kurses');
      handleAPIError(err);
      throw err;
    } finally {
      setPublishingCourse(false);
    }
  };

  const handleUnpublishCourse = async () => {
    if (course === null) return;
    if (!confirm('Möchten Sie diesen Kurs wirklich zurückziehen?')) {
      return;
    }

    try {
      setPublishingCourse(true);
      const updatedCourse = await APIRoutes.unpublishCourse(course.id);
      setCourse(updatedCourse);
      showSuccessToast('Kurs erfolgreich zurückgezogen');
    } catch (err) {
      console.error('Error unpublishing course:', err);
      showErrorToast('Fehler beim Zurückziehen des Kurses');
      handleAPIError(err);
    } finally {
      setPublishingCourse(false);
    }
  };

  const handleDeleteCourse = async () => {
    if (course === null) return;
    if (!confirm('Sind Sie sicher, dass Sie diesen Kurs löschen möchten? Alle Module und Videos werden ebenfalls gelöscht.')) {
      return;
    }

    try {
      setDeletingCourse(true);
      await APIRoutes.deleteCourse(course.id);
      showSuccessToast('Kurs erfolgreich gelöscht');
      navigate('/admin/courses');
    } catch (err) {
      console.error('Error deleting course:', err);
      showErrorToast('Fehler beim Löschen des Kurses');
      handleAPIError(err);
      setDeletingCourse(false);
    }
  };

  // Show fullscreen error if critical error occurred
  if (criticalError) {
    return (
      <FullscreenError
        title="Kurs nicht verfügbar"
        message={error || 'Die Kursdetails konnten nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar.'}
        onRetry={fetchCourseAndModules}
      />
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Link
              to="/admin/courses"
              className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {loading ? 'Lade Kurs...' : course?.title || 'Kurs nicht gefunden'}
            </h1>
          </div>
          {course !== null && (
            <div className="flex items-center space-x-2">
              {course.published ? (
                <button
                  onClick={handleUnpublishCourse}
                  disabled={!isStaff || publishingCourse}
                  title={isStaff ? 'Kurs zurückziehen' : 'Nur für Mitarbeiter'}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg border border-yellow-300 dark:border-yellow-700 text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 transition-colors ${!isStaff || publishingCourse ? 'opacity-50 cursor-not-allowed' : 'hover:bg-yellow-100 dark:hover:bg-yellow-900/40 cursor-pointer'}`}
                >
                  <FontAwesomeIcon icon={publishingCourse ? faSpinner : faEyeSlash} className={`w-4 h-4 ${publishingCourse ? 'animate-spin' : ''}`} />
                  <span>Zurückziehen</span>
                </button>
              ) : (
                <button
                  onClick={() => setIsPublishCourseModalOpen(true)}
                  disabled={!isStaff || publishingCourse}
                  title={isStaff ? 'Kurs veröffentlichen' : 'Nur für Mitarbeiter'}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg border border-green-300 dark:border-green-700 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 transition-colors ${!isStaff || publishingCourse ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-100 dark:hover:bg-green-900/40 cursor-pointer'}`}
                >
                  <FontAwesomeIcon icon={publishingCourse ? faSpinner : faRocket} className={`w-4 h-4 ${publishingCourse ? 'animate-spin' : ''}`} />
                  <span>Veröffentlichen</span>
                </button>
              )}
              <button
                onClick={() => setIsEditCourseModalOpen(true)}
                disabled={!isStaff}
                title={isStaff ? 'Kurs bearbeiten' : 'Nur für Mitarbeiter'}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 transition-colors ${!isStaff ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer'}`}
              >
                <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                <span>Bearbeiten</span>
              </button>
              <button
                onClick={handleDeleteCourse}
                disabled={!isStaff || deletingCourse}
                title={isStaff ? 'Kurs löschen' : 'Nur für Mitarbeiter'}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 transition-colors ${!isStaff || deletingCourse ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-100 dark:hover:bg-red-900/40 cursor-pointer'}`}
              >
                <FontAwesomeIcon icon={deletingCourse ? faSpinner : faTrash} className={`w-4 h-4 ${deletingCourse ? 'animate-spin' : ''}`} />
                <span>Löschen</span>
              </button>
            </div>
          )}
        </div>

        {loading && (
          <div className="flex justify-center items-center py-12">
            <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
          </div>
        )}

        {error && !criticalError && (
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {!loading && course && (
          <>
            {/* Course Overview */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 mb-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {course.thumbnail && (
                  <div className="lg:col-span-1">
                    <div className="aspect-video relative overflow-hidden rounded-lg">
                      <img
                        src={APIRoutes.getBlobContentUrl(course.thumbnail)}
                        alt={course.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                )}
                <div className={course.thumbnail ? "lg:col-span-2" : "lg:col-span-3"}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        {course.title}
                      </h2>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        course.published 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      }`}>
                        {course.published ? 'Veröffentlicht' : 'Entwurf'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <MarkdownRenderer>
                      {course.description}
                    </MarkdownRenderer>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="block font-medium text-gray-500 dark:text-gray-400">Module</span>
                      <span className="text-lg font-bold text-gray-900 dark:text-white">{course.moduleCount}</span>
                    </div>
                    <div>
                      <span className="block font-medium text-gray-500 dark:text-gray-400">Videos</span>
                      <span className="text-lg font-bold text-gray-900 dark:text-white">{course.videoCount}</span>
                    </div>
                    <div>
                      <span className="block font-medium text-gray-500 dark:text-gray-400">Erstellt</span>
                      <span className="text-lg font-bold text-gray-900 dark:text-white">{formatDate(course.created)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabbed Card: Modules / Users */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
              <div className="border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between px-6">
                  <nav className="flex -mb-px space-x-6" aria-label="Tabs">
                    <button
                      onClick={() => setActiveTab('modules')}
                      className={`flex items-center space-x-2 py-4 border-b-2 text-sm font-medium transition-colors cursor-pointer ${
                        activeTab === 'modules'
                          ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                      }`}
                    >
                      <FontAwesomeIcon icon={faBook} className="w-4 h-4" />
                      <span>Module ({totalModules})</span>
                    </button>
                    <button
                      onClick={() => setActiveTab('users')}
                      className={`flex items-center space-x-2 py-4 border-b-2 text-sm font-medium transition-colors cursor-pointer ${
                        activeTab === 'users'
                          ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                      }`}
                    >
                      <FontAwesomeIcon icon={faUsers} className="w-4 h-4" />
                      <span>Benutzer mit Zugriff</span>
                    </button>
                  </nav>
                  {activeTab === 'modules' && (
                    <button
                      onClick={() => setIsCreateModuleModalOpen(true)}
                      disabled={!isStaff}
                      title={isStaff ? 'Neues Modul erstellen' : 'Nur für Mitarbeiter'}
                      className={`bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors ${!isStaff ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700 cursor-pointer'}`}
                    >
                      <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
                      <span>Neues Modul</span>
                    </button>
                  )}
                </div>
              </div>

              <div className="p-6">
                {activeTab === 'users' ? (
                  <CourseUsersTab courseId={course.id} />
                ) : modules.length === 0 ? (
                  <div className="text-center py-12">
                    <FontAwesomeIcon icon={faBook} className="w-16 h-16 text-gray-400 dark:text-gray-600 mb-4" />
                    <h4 className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-2">
                      Keine Module gefunden
                    </h4>
                    <p className="text-gray-500 dark:text-gray-500 mb-6">
                      Erstellen Sie das erste Modul für diesen Kurs.
                    </p>
                    <button
                      onClick={() => setIsCreateModuleModalOpen(true)}
                      disabled={!isStaff}
                      title={isStaff ? 'Modul erstellen' : 'Nur für Mitarbeiter'}
                      className={`bg-blue-600 text-white px-6 py-3 rounded-lg transition-colors ${!isStaff ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700 cursor-pointer'}`}
                    >
                      Modul erstellen
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {modules.filter(module => module !== null && module !== undefined).map((module) => (
                        <div key={module.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 overflow-hidden relative transition-colors">
                          <Link
                            to={`/admin/courses/${courseId}/modules/${module.id}`}
                            className="block"
                          >
                            <div className="aspect-video relative overflow-hidden">
                              {module.thumbnail ? (
                                <img
                                  src={APIRoutes.getBlobContentUrl(module.thumbnail)}
                                  alt={module.title}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                                  <FontAwesomeIcon icon={faBook} className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                                </div>
                              )}
                            </div>

                            <div className="p-6">
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex-1">
                                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                    {module.title}
                                  </h3>
                                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-3">
                                    <MarkdownRenderer>
                                      {module.description}
                                    </MarkdownRenderer>
                                  </div>
                                </div>
                                <div className="flex items-center ml-2">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    module.published
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                  }`}>
                                    {module.published ? 'Veröffentlicht' : 'Entwurf'}
                                  </span>
                                </div>
                              </div>

                              <div className="flex items-center justify-between mb-4 text-sm text-gray-500 dark:text-gray-400">
                                <div className="flex items-center space-x-4">
                                  <div className="flex items-center">
                                    <FontAwesomeIcon icon={faVideo} className="w-4 h-4 mr-1" />
                                    <span>{module.videoCount} Videos</span>
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  Reihenfolge: {module.order}
                                </div>
                              </div>
                            </div>
                          </Link>

                          {/* Action buttons positioned absolutely to prevent Link click */}
                          <div className="absolute bottom-4 right-6 flex items-center space-x-3">
                            {/* Publish/Unpublish Button */}
                            {module.published ? (
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleUnpublishModule(module.id);
                                }}
                                disabled={!isStaff || publishingModule === module.id}
                                className={`text-yellow-600 dark:text-yellow-400 disabled:opacity-50 bg-white dark:bg-gray-800 rounded-full p-2 shadow-sm ${!isStaff ? 'cursor-not-allowed' : 'hover:text-yellow-800 dark:hover:text-yellow-300 cursor-pointer'}`}
                                title={isStaff ? 'Zurückziehen' : 'Nur für Mitarbeiter'}
                              >
                                {publishingModule === module.id ? (
                                  <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                                ) : (
                                  <FontAwesomeIcon icon={faEyeSlash} className="w-4 h-4" />
                                )}
                              </button>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setPublishModalModule(module);
                                }}
                                disabled={!isStaff || publishingModule === module.id}
                                className={`text-green-600 dark:text-green-400 disabled:opacity-50 bg-white dark:bg-gray-800 rounded-full p-2 shadow-sm ${!isStaff ? 'cursor-not-allowed' : 'hover:text-green-800 dark:hover:text-green-300 cursor-pointer'}`}
                                title={isStaff ? 'Veröffentlichen' : 'Nur für Mitarbeiter'}
                              >
                                {publishingModule === module.id ? (
                                  <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                                ) : (
                                  <FontAwesomeIcon icon={faRocket} className="w-4 h-4" />
                                )}
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleEditModule(module);
                              }}
                              disabled={!isStaff}
                              className={`text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-full p-2 shadow-sm ${!isStaff ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-800 dark:hover:text-gray-200 cursor-pointer'}`}
                              title={isStaff ? 'Bearbeiten' : 'Nur für Mitarbeiter'}
                            >
                              <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDeleteModule(module.id);
                              }}
                              disabled={!isStaff || deletingModule === module.id}
                              className={`text-red-600 dark:text-red-400 disabled:opacity-50 bg-white dark:bg-gray-800 rounded-full p-2 shadow-sm ${!isStaff ? 'cursor-not-allowed' : 'hover:text-red-800 dark:hover:text-red-300 cursor-pointer'}`}
                              title={isStaff ? 'Löschen' : 'Nur für Mitarbeiter'}
                            >
                              {deletingModule === module.id ? (
                                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                              ) : (
                                <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <PageBasedPagination
                      currentPage={currentPage}
                      totalCount={totalModules}
                      pageSize={PAGE_SIZE}
                      onPageChange={setCurrentPage}
                      loading={loading}
                    />
                  </>
                )}
              </div>
            </div>
          </>
        )}

      {/* Modals */}
      {course && (
        <>
          <CreateModuleModal
            isOpen={isCreateModuleModalOpen}
            onClose={() => setIsCreateModuleModalOpen(false)}
            courseId={course.id}
            onModuleCreated={handleModuleCreated}
            courseThumbnail={course.thumbnail ?? undefined}
            courseNoKnowledge={course.noKnowledge}
          />

          <EditModuleModal
            isOpen={isEditModuleModalOpen}
            onClose={() => setIsEditModuleModalOpen(false)}
            courseId={course.id}
            module={editingModule}
            onModuleUpdated={handleModuleUpdated}
          />

          <EditCourseModal
            isOpen={isEditCourseModalOpen}
            onClose={() => setIsEditCourseModalOpen(false)}
            course={course}
            onCourseUpdated={handleCourseUpdated}
          />

          <PublishModal
            isOpen={isPublishCourseModalOpen}
            onClose={() => setIsPublishCourseModalOpen(false)}
            onPublish={handlePublishCourse}
            title="Kurs veröffentlichen"
            itemName={course.title}
            type="course"
          />
        </>
      )}

      {/* Publish Module Modal */}
      {publishModalModule && (
        <PublishModal
          isOpen={!!publishModalModule}
          onClose={() => setPublishModalModule(null)}
          onPublish={handlePublishModule}
          title="Modul veröffentlichen"
          itemName={publishModalModule.title}
          type="module"
        />
      )}
    </>
  );
}
